import { Component, Input, forwardRef, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { getDocument, PDFDocumentProxy, GlobalWorkerOptions } from 'pdfjs-dist';
import { environment } from '../../../services/global-environment';
import { GuidService } from '../../../services/guid.service';
import { PdfConfigService } from '../../../services/pdf-config.service';
import { firstValueFrom } from 'rxjs';

// 使用統一的 PDF 配置服務
PdfConfigService.initializePdfWorker();

export interface FileDisplayItem {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  fileUrl?: string;
  blobName?: string;
  imagePages?: string[]; // PDF 轉換後的圖片頁面
  uploading?: boolean;
  uploadProgress?: number;
  error?: string;
}

@Component({
  selector: 'app-file-display',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule],
  template: `
    <div class="file-display-container">
      <label [for]="id" [class.required-field]="required">{{ label }}</label>
      
      <!-- 檔案上傳區域 - 只在預覽模式且沒有檔案時顯示 -->
      <div class="file-upload-area" 
           *ngIf="preview && displayFiles.length === 0"
           [class.drag-over]="isDragOver"
           [class.disabled]="disabled"
           (dragover)="onDragOver($event)"
           (dragleave)="onDragLeave($event)"
           (drop)="onFileDrop($event)"
           (click)="triggerFileSelect()">
        
        <div class="upload-content">
          <i class="fas fa-cloud-upload-alt upload-icon"></i>
          <p class="upload-text">拖拽檔案到此處或點擊選擇檔案</p>
          <p class="upload-hint">支援 PDF、JPG、PNG、GIF 格式，最大 10MB</p>
        </div>
        
        <input #fileInput
               type="file"
               [id]="id"
               [name]="name"
               (change)="onFileSelected($event)"
               accept=".pdf,.jpg,.jpeg,.png,.gif"
               multiple
               hidden>
      </div>

      <!-- 檔案列表 - 只在預覽模式時顯示 -->
      <div class="file-list" *ngIf="preview && files.length > 0">
        <div class="file-item" *ngFor="let file of files; let i = index">
          
          <!-- 檔案資訊 -->
          <div class="file-info">
            <div class="file-name">
              <i [class]="getFileIcon(file.fileType)"></i>
              {{ file.fileName }}
            </div>
            <div class="file-size">{{ formatFileSize(file.fileSize) }}</div>
          </div>

          <!-- 上傳進度 -->
          <div class="upload-progress" *ngIf="file.uploading">
            <div class="progress-bar">
              <div class="progress-fill" [style.width.%]="file.uploadProgress || 0"></div>
            </div>
            <span class="progress-text">{{ file.uploadProgress || 0 }}%</span>
          </div>

          <!-- 錯誤訊息 -->
          <div class="error-message" *ngIf="file.error">
            <i class="fas fa-exclamation-triangle"></i>
            {{ file.error }}
          </div>

          <!-- 操作按鈕 -->
          <div class="file-actions">
            <button type="button" 
                    class="btn btn-danger btn-sm"
                    (click)="removeFile(i)"
                    [disabled]="file.uploading">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- 檔案內容顯示區域 - 當有檔案時顯示 -->
      <div class="file-content-display" *ngIf="displayFiles.length > 0">
        <div class="file-display-item" *ngFor="let file of displayFiles">
          
          <!-- 圖片檔案直接顯示 -->
          <div class="image-display" *ngIf="isImageFile(file.fileType) && file.fileUrl">
            <img [src]="file.fileUrl" 
                 [alt]="file.fileName"
                 class="display-image"
                 (error)="onImageError($event, file)">
          </div>

          <!-- PDF 檔案顯示轉換後的圖片頁面 -->
          <div class="pdf-display" *ngIf="isPdfFile(file.fileType) && file.imagePages && file.imagePages.length > 0">
            <div class="pdf-page" *ngFor="let pageImage of file.imagePages; let pageIndex = index">
              <div class="page-number">第 {{ pageIndex + 1 }} 頁</div>
              <img [src]="pageImage" 
                   [alt]="file.fileName + ' - 第' + (pageIndex + 1) + '頁'"
                   class="pdf-page-image"
                   (error)="onImageError($event, file)">
            </div>
          </div>

          <!-- 處理中狀態 -->
          <div class="processing-state" *ngIf="isPdfFile(file.fileType) && file.fileUrl && (!file.imagePages || file.imagePages.length === 0)">
            <div class="processing-spinner">
              <i class="fas fa-spinner fa-spin"></i>
            </div>
            <p>正在處理 PDF 檔案...</p>
          </div>
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileDisplayComponent),
      multi: true
    }
  ],
  styleUrls: ['./file-display.component.scss']
})
export class FileDisplayComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label: string = '檔案上傳';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() maxFileSize: number = 10; // MB
  @Input() allowedTypes: string[] = ['.pdf', '.jpg', '.jpeg', '.png', '.gif'];
  @Input() preview: boolean = false; // 新增：是否為預覽模式

  files: FileDisplayItem[] = [];
  displayFiles: FileDisplayItem[] = [];
  isDragOver: boolean = false;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(
    private http: HttpClient,
    private guidService: GuidService
  ) {}

  ngOnInit(): void {
    if (!this.id) {
      this.id = this.guidService.generateGuid();
    }
  }

  ngOnDestroy(): void {
    // 清理資源
  }

  // 私有方法：使用統一的 Worker 驗證
  private async verifyWorker(): Promise<boolean> {
    return PdfConfigService.verifyWorker();
  }

  // ControlValueAccessor 實作
  writeValue(value: FileDisplayItem[]): void {
    if (value) {
      this.files = [...value];
      this.displayFiles = this.files.filter(f => f.fileUrl && !f.uploading);
      console.log('File display component writeValue called:', {
        value,
        files: this.files,
        displayFiles: this.displayFiles,
        preview: this.preview
      });
    } else {
      this.files = [];
      this.displayFiles = [];
      console.log('File display component writeValue called with empty value');
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // 拖拽事件處理
  onDragOver(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    this.isDragOver = false;
    
    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(files);
    }
  }

  // 檔案選擇處理
  triggerFileSelect(): void {
    if (this.disabled) return;
    const fileInput = document.getElementById(this.id) as HTMLInputElement;
    fileInput?.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(input.files);
      input.value = ''; // 清空以允許重複選擇相同檔案
    }
  }

  // 處理檔案
  private async handleFiles(fileList: FileList): Promise<void> {
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      
      if (this.validateFile(file)) {
        const fileItem: FileDisplayItem = {
          id: this.guidService.generateGuid(),
          fileName: file.name,
          fileType: file.type || this.getFileTypeFromExtension(file.name),
          fileSize: file.size,
          uploading: true,
          uploadProgress: 0
        };

        this.files.push(fileItem);
        this.emitChange();

        // 開始上傳
        await this.uploadFile(file, fileItem);
      }
    }
  }

  // 檔案驗證
  private validateFile(file: File): boolean {
    // 檢查檔案格式
    const fileExtension = this.getFileExtension(file.name);
    if (!this.allowedTypes.includes(fileExtension.toLowerCase())) {
      alert(`不支援的檔案格式：${fileExtension}。支援格式：${this.allowedTypes.join(', ')}`);
      return false;
    }

    // 檢查檔案大小
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > this.maxFileSize) {
      alert(`檔案大小超過限制。最大允許 ${this.maxFileSize}MB，當前檔案 ${fileSizeMB.toFixed(2)}MB`);
      return false;
    }

    return true;
  }

  // 上傳檔案到 Azure Storage
  private async uploadFile(file: File, fileItem: FileDisplayItem): Promise<void> {
    try {
      const formData = new FormData();
      const companyid = environment.companyid;
      const filename = `file_${Date.now()}_${file.name}`;

      formData.append('file', file);
      formData.append('companyid', companyid);
      formData.append('filename', filename);
      formData.append('returnTempUrl', 'true');

      const response = await firstValueFrom(this.http.post<any>(`${environment.apiUrl}/api/storage/upload`, formData));

      if (response && response.success) {
        fileItem.blobName = response.data.blobName;
        fileItem.fileUrl = response.data.tempUrl;
        fileItem.uploading = false;
        fileItem.uploadProgress = 100;

        // 如果是 PDF 檔案，開始轉換為圖片
        if (this.isPdfFile(fileItem.fileType)) {
          await this.convertPdfToImages(file, fileItem);
        }

        // 更新顯示列表
        this.updateDisplayFiles();
        this.emitChange();
      } else {
        throw new Error(response?.message || '上傳失敗');
      }
    } catch (error) {
      console.error('檔案上傳失敗:', error);
      fileItem.uploading = false;
      fileItem.error = '上傳失敗：' + (error as Error).message;
      this.emitChange();
    }
  }

  // PDF 轉圖片 - 改善版本，支援更好的記憶體管理和錯誤處理
  private async convertPdfToImages(file: File, fileItem: FileDisplayItem): Promise<void> {
    let pdf: PDFDocumentProxy | null = null;
    
    try {
      console.log('開始處理 PDF 檔案:', file.name, '大小:', file.size);
      
      const arrayBuffer = await file.arrayBuffer();
      
      // 首先嘗試驗證Worker是否可用
      try {
        await this.verifyWorker();
        console.log('Worker 初始化驗證成功');
      } catch (workerError) {
        console.warn('Worker 驗證失敗，將使用預設配置:', workerError);
        // 重新初始化PDF配置
        PdfConfigService.initializePdfWorker();
      }
      
      // 使用基本的 PDF 載入配置，避免路徑問題
      pdf = await getDocument({
        data: arrayBuffer,
        disableAutoFetch: false,
        disableStream: false,
        disableRange: false,
        useSystemFonts: true
      }).promise;

      console.log('PDF 載入成功，總頁數:', pdf.numPages);
      
      const imagePages: string[] = [];
      const maxPages = Math.min(pdf.numPages, 50); // 限制最大處理頁數避免記憶體問題
      
      // 序列化處理每一頁，避免記憶體溢出
      for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
        console.log(`處理第 ${pageNum} 頁 / 共 ${maxPages} 頁`);
        
        let page = null;
        let canvas = null;
        
        try {
          // 獲取頁面
          page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.2 }); // 降低縮放比例節省記憶體
          
          // 建立 canvas
          canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          
          if (!context) {
            throw new Error(`無法建立 Canvas 上下文 (第 ${pageNum} 頁)`);
          }
          
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          // 渲染頁面
          const renderTask = page.render({
            canvasContext: context,
            viewport: viewport
          });

          await renderTask.promise;

          // 轉換為圖片
          const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8); // 使用 JPEG 格式降低記憶體使用
          imagePages.push(imageDataUrl);
          
          console.log(`第 ${pageNum} 頁處理完成`);
          
        } catch (pageError) {
          console.error(`處理第 ${pageNum} 頁時發生錯誤:`, pageError);
          // 繼續處理下一頁，不中斷整個過程
          continue;
        } finally {
          // 清理資源
          if (page) {
            page.cleanup();
          }
          if (canvas) {
            canvas.remove();
            canvas = null;
          }
          
          // 強制垃圾回收（在支援的環境中）
          if (typeof window !== 'undefined' && 'gc' in window) {
            (window as any).gc();
          }
        }
        
        // 每處理 5 頁後稍微延遲，避免阻塞UI
        if (pageNum % 5 === 0) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }

      if (imagePages.length === 0) {
        throw new Error('無法處理任何 PDF 頁面');
      }

      console.log(`PDF 處理完成，成功處理 ${imagePages.length} 頁`);

      fileItem.imagePages = imagePages;
      this.updateDisplayFiles();
      this.emitChange();
      
    } catch (error) {
      console.error('PDF 轉換失敗:', error);
      
      // 使用統一的錯誤訊息處理
      const errorMessage = PdfConfigService.getPdfErrorMessage(error as Error);
      fileItem.error = errorMessage;
      this.emitChange();
      
    } finally {
      // 最終清理
      if (pdf) {
        try {
          pdf.destroy();
        } catch (destroyError) {
          console.warn('清理 PDF 資源時發生錯誤:', destroyError);
        }
      }
    }
  }

  // 移除檔案
  removeFile(index: number): void {
    this.files.splice(index, 1);
    this.updateDisplayFiles();
    this.emitChange();
  }

  // 更新顯示檔案列表
  private updateDisplayFiles(): void {
    this.displayFiles = this.files.filter(f => {
      return f.fileUrl && !f.uploading && !f.error;
    });
  }

  // 工具方法
  private getFileExtension(fileName: string): string {
    return '.' + fileName.split('.').pop()?.toLowerCase() || '';
  }

  private getFileTypeFromExtension(fileName: string): string {
    const ext = this.getFileExtension(fileName);
    switch (ext) {
      case '.pdf':
        return 'application/pdf';
      case '.jpg':
      case '.jpeg':
        return 'image/jpeg';
      case '.png':
        return 'image/png';
      case '.gif':
        return 'image/gif';
      default:
        return 'application/octet-stream';
    }
  }

  getFileIcon(fileType: string): string {
    if (this.isPdfFile(fileType)) {
      return 'fas fa-file-pdf text-danger';
    } else if (this.isImageFile(fileType)) {
      return 'fas fa-file-image text-success';
    }
    return 'fas fa-file text-secondary';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  isImageFile(fileType: string): boolean {
    return fileType.startsWith('image/');
  }

  isPdfFile(fileType: string): boolean {
    return fileType === 'application/pdf';
  }

  onImageError(event: Event, file: FileDisplayItem): void {
    console.error('圖片載入失敗:', file.fileName);
    file.error = '圖片載入失敗';
  }

  private emitChange(): void {
    this.onChange(this.files);
    this.onTouched();
  }
}